import {combineReducers} from "redux";
import gridConfigReducer from "./gridConfigReducer";
import sharedGameStateReducer, {INITIAL_SHARED_GAME_STATE} from "./sharedGameStateReducer";
import {createPlayersState} from "../../../utils/gameState/createPlayersState";
import {checkIfPlayerBonusPerCityRule} from "../../../utils/gameState/checkIfPlayerBonusPerCityRule";
import {calculateCitiesCache} from "../../../utils/gameState/calculateCitiesCache";
import {updatePlayersIncome} from "../../../utils/gameState/updatePlayersIncome";
import {PHASES} from "../../../constants/phases";
import {SPECIAL_BUILDING_TOKENS, TOKENS, UNDESTRUCTIBLE_TOKENS} from "../../../constants/tokens";
import {FORTRESS_MAX_DAMAGE} from "../../../configs/specialBuildings";
import hexesReducer from "./hexesReducer";
import {processCityCaptures} from "../../../utils/gameState/checkCityCapture";
import {buildCityCaptureStats} from "../../../utils/gameState/buildCityCaptureStats";
import {buildCombatStats, sumMeleeDamageByPlayer} from "../../../utils/gameState/buildCombatStats";
import {getCityCaptureAnimationDurationMs} from "../../../configs/cityCaptureAnimation";
import {updateWinPointsForCitiesCapture} from "../../../utils/gameState/updateWinPointsForCitiesCapture";
import {calcUnitUpdateBasedOnPendingSupply} from "../../../utils/gameState/supply/calcUnitUpdateBasedOnPendingSupply";
import {checkIfOnWorkingBarracks} from "../../../utils/gameState/checkIfOnWorkingBarracks";
import {checkIfSpecialBuilding} from "../../../utils/buildings/checkIfSpecialBuilding";
import {checkIfCentralQuarter} from "../../../utils/buildings/checkIfCentralQuarter";
import {isBuildingDestroyed} from "../../../utils/gameState/shooting/buildingDamageUtils";
import {
  checkIfAdjacentToWorkingMilitaryCamp
} from "../../../utils/gameState/specialBuildings/checkIfAdjacentToWorkingMilitaryCamp";
import {
  checkIfHasRemainingConstructionActions
} from "../../../utils/gameState/construction/checkIfHasRemainingConstructionActions";
import {calcCityWinPoints} from "../../../utils/gameState/winPoints/calcCityWinPoints";
import {calcPendingBuildingsWinPoints} from "../../../utils/gameState/winPoints/calcPendingBuildingsWinPoints";
import {isEnvConfEnabled} from "../../../utils/env/isEnvConfEnabled";
import {ENV_CONFIG_VARIABLES} from "../../../constants/env";
import {getUnitTypeConfig} from "../../../configs/units";
import {COMBAT_TYPE, FORTRESS_MELEE_DAMAGE_RATION} from "../../../constants/combat";
import {D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE} from "../../../configs/war";
import {CONSTRUCTION_ACTION_TYPES} from "../../../constants/construction";
import {calcUnitMeleeDamageWithFortress} from "../../../utils/damage/calcUnitReceivedDamage";
import {GAME_TYPES} from "../../../constants/game";
import {getRulesGameType} from "../../../utils/gameState/getRulesGameType";
import {getRuinsIncomeRoundLimit} from "../../../utils/gameState/income/checkIfRoundWithIncreasedRuinsIncome";
import {checkIfCitylessEconomyRule} from "../../../utils/gameState/income/checkIfCitylessEconomyRule";
import {checkIfCentralQuarterFortressDamageRule} from "../../../utils/gameState/shooting/checkIfCentralQuarterFortressDamageRule";
import {checkIfBuildingWinPointsInCitiesRule} from "../../../utils/gameState/winPoints/checkIfBuildingWinPointsInCitiesRule";
import {checkIfOwner} from "../../../utils/gameState/checkIfOwner";
import {NEUTRAL_PLAYER_ID} from "../../../constants/players";
import {calcMeleeDamage} from "../../../utils/damage/calcMeleeDamage";
import {getMeleeCountedDiceRolls} from "../../../utils/melee/getMeleeDiceRolls";
import {getTurnOrderFromPlayer} from "../../../utils/players/getTurnOrderFromPlayer";
import {getPlayerById} from "../../../utils/players/getPlayerById";
import {calculateOrcLoss} from "../../../utils/factions/orcs/calculateOrcLoss";
import {isUnitFaction} from "../../../utils/factions/isUnitFaction";
import {FACTIONS} from "../../../constants/factions";
import {
  calculateOrcMeleeDamageForElimination
} from "../../../utils/factions/orcs/calculateOrcMeleeDamageForElimination";
import {isPlayerPolicy} from "../../../utils/factions/isPlayerPolicy";
import {POLICIES} from "../../../constants/policies";
import {calcWinPointsForKill} from "../../../utils/gameState/winPoints/calcWinPointsForKill";
import {POLICY_BARBARISM_MELEE_DAMAGE_WIN_POINTS_BONUS} from "../../../configs/policy/barbarism";
import policyAgrarianismReducer from "./policyAgrarianismReducer";
import giantsAdditionalSupplyReducer from "./giantsAdditionalSupplyReducer";
import {isStalePhaseAction} from "../../../utils/gameState/turn/phaseActionGuard";
import {GIANTS_ADDITIONAL_SUPPLY_COST} from "../../../configs/factions/giants";
import {checkIfUnitHasPendingAdditionalSupply} from "../../../utils/gameState/checkIfUnitHasPendingAdditionalSupply";
import {checkIfUnitHasPendingBasicSupply} from "../../../utils/gameState/checkIfUnitHasPendingBasicSupply";
import {getHexDistance} from "../../../utils/coords/getHexDistance";
import {GOBLINS_ABILITY_RADIUS, GOBLINS_MIN_UNIT_SIZE_FOR_ABILITY} from "../../../configs/factions/goblins";
import {calculateGoblinsBuildingDamageOutcome} from "../../../utils/factions/goblins/calculateGoblinsBuildingDamageOutcome";
import {
  checkIfAllPlayersCompletedPhase,
  checkIfParallelSupplyPhase,
  checkIfSupplyActorLocked,
  resolveSupplyActorId
} from "../../../utils/gameState/supply/parallelSupply";

// Выбор фермы к обмену по аграризму и великана для доп. снабжения хранится по
// игрокам (в одновременной фазе снабжения выбирают все разом). Вложенные
// редьюсеры этих срезов не видят состояния партии, поэтому игрока за них
// определяет обертка: экшен без playerId (старые записи матчей) относится к
// текущему игроку
const SUPPLY_SELECTION_ACTION_TYPES = [
  'POLICY_AGRARIANISM_SWITCH_CONVERSION_ON',
  'POLICY_AGRARIANISM_SWITCH_CONVERSION_OFF',
  'POLICY_AGRARIANISM_SELECT_FARM_FOR_CONVERSION',
  'POLICY_AGRARIANISM_COMMIT_CONVERSION',
  'GIANTS_SELECT_UNIT_FOR_ADDITIONAL_SUPPLY',
  'GIANTS_RESET_ADDITIONAL_SUPPLY_SELECTION',
];

// Персональное время хода из START_GAME (промо-матч против ИИ): берем только
// записи с положительной длительностью и приводим к одному виду. Чужой мусор
// в payload (запись, собранная вручную, старая версия сервера) в состояние
// партии не попадает — пустой объект означает «у всех общий таймер»
const sanitizePlayerTurnTimeOverrides = (overrides) => {
  if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) {
    return {};
  }
  const result = {};
  Object.keys(overrides).forEach((playerId) => {
    const override = overrides[playerId];
    const durationMs = override?.durationMs;
    if (!playerId || !Number.isFinite(durationMs) || durationMs <= 0) {
      return;
    }
    result[playerId] = {durationMs, isActive: Boolean(override.isActive)};
  });
  return result;
};

const withResolvedSupplyActor = (action, gameState) => ({
  ...action,
  payload: {
    ...(action.payload || {}),
    playerId: resolveSupplyActorId(gameState, action.payload),
  },
});

export const internalReducer = combineReducers({
  gridConfig: gridConfigReducer,
  hexes: hexesReducer,
  gameState: sharedGameStateReducer,
  // POLICIES
  policyAgrarianism: policyAgrarianismReducer,
  // FACTIONS
  giantsAdditionalSupply: giantsAdditionalSupplyReducer
});

const sharedStateReducer = (state, action) => {
  switch (action.type) {
    case 'RESET_STATE':
      return internalReducer(undefined, action);
    case 'LOAD_STATE': {
      const {loadedState, finalRound} = action.payload;
      if (!loadedState) return state;

      // Загружаем hexes из файла
      const hexes = loadedState.hexes || state.hexes;

      const players = loadedState.players || loadedState.gameState?.players || state.gameState.players;

      // Загружаем units из файла (если есть); состояние предыдущей партии
      // в новую не протекает — база чистая
      const units = loadedState.units || loadedState.gameState?.units || [];

      // Загружаем specialBuildings из файла (если есть)
      const specialBuildings = loadedState.specialBuildings
        || loadedState.gameState?.specialBuildings || {};

      // Пересчитываем citiesCache на основе загруженных hexes и specialBuildings
      const citiesCache = calculateCitiesCache(hexes, specialBuildings);

      // Обновляем gameState с загруженными units.
      // mountainTiles — карта «ячейка -> вариант картинки горы», которую
      // недолго хранили в состоянии партии: она осталась в записях матчей тех
      // дней и может прийти со старого сервера. Картинку горы теперь задает
      // подложка (configs/mountainTiles), и в состояние карта не попадает
      // eslint-disable-next-line no-unused-vars
      const {mountainTiles: legacyMountainTiles, ...loadedGameState} = (
        loadedState.gameState || loadedState.sharedGameState || {}
      );
      const turnEndTimestamp = loadedState.turnEndTimestamp ?? loadedGameState.turnEndTimestamp;
      const policyMarket = loadedState.policyMarket ?? loadedGameState.policyMarket ?? state.gameState.policyMarket ?? [];
      const timerEnabled = loadedState.timerEnabled ?? loadedGameState.timerEnabled ?? true;
      const fastMode = loadedState.fastMode ?? loadedGameState.fastMode ?? false;
      // Подложка поля переживает загрузку состояния: у обучения и сюжетных
      // миссий START_GAME может не быть вовсе (грузится снимок), а при
      // переподключении посреди матча она приезжает в gameState с сервера
      const groundBackground = loadedState.groundBackground
        ?? loadedGameState.groundBackground ?? state.gameState.groundBackground ?? null;
      // Лимит раундов партии (см. checkIfFinalRound): число или null — миссия
      // без лимита. Загрузка поля из меню игры лимита не передает — партия
      // сохраняет свой, а не остается вовсе без последнего раунда
      const nextFinalRound = finalRound !== undefined ? finalRound : state.gameState.finalRound;

      // База — НАЧАЛЬНОЕ состояние партии, а не предыдущее: загрузка нового
      // поля не должна наследовать gameResults/phase/round прошлой игры
      // (дефект демо-партии меню: боты не ходили или расставлялись не в фазе
      // расстановки). Полные состояния (онлайн/сохранения) перекрывают базу
      // целиком через loadedGameState.
      const nextGameState = {
        ...INITIAL_SHARED_GAME_STATE,
        ...loadedGameState,
        // Тип игры выставляется отдельным экшеном до загрузки поля — сохраняем
        gameType: loadedGameState.gameType ?? state.gameState.gameType,
        // Правила просмотра записи задает только setGameType, не снимок поля
        // (см. getRulesGameType)
        replayRulesGameType: state.gameState.replayRulesGameType ?? null,
        // Тумблер планировщика ботов тоже выставляется до загрузки поля
        // (App.handleHotSeatPlayersSubmit); демо меню сбрасывает его явно
        plannerBotsEnabled: loadedGameState.plannerBotsEnabled
          ?? state.gameState.plannerBotsEnabled ?? false,
        turnEndTimestamp,
        policyMarket,
        timerEnabled,
        fastMode,
        groundBackground,
        players,
        units: units,
        citiesCache: citiesCache,
        specialBuildings: specialBuildings,
        finalRound: nextFinalRound,
        // Метку правил ставит loadState(); полное состояние с сервера (клиент
        // MCP кладет его в LOAD_STATE как есть) несет ее в самом gameState.
        // Нет ни там, ни там — это запись, сделанная до правила: окно дохода
        // руин от FINAL_ROUND (см. getRuinsIncomeRoundLimit)
        isRuinsIncomeByRoundLimit: Boolean(
          action.payload.isRuinsIncomeByRoundLimit ?? loadedGameState.isRuinsIncomeByRoundLimit
        ),
        // Так же и правило «без городов — без монет» (checkIfCitylessEconomyRule)
        isCitylessEconomyRule: Boolean(
          action.payload.isCitylessEconomyRule ?? loadedGameState.isCitylessEconomyRule
        ),
        // И бонус дохода за каждый город (checkIfPlayerBonusPerCityRule)
        isPlayerBonusPerCityRule: Boolean(
          action.payload.isPlayerBonusPerCityRule ?? loadedGameState.isPlayerBonusPerCityRule
        ),
        // И досрочный конец «в живых один» (checkIfLastStandingEndRule)
        isLastStandingEndRule: Boolean(
          action.payload.isLastStandingEndRule ?? loadedGameState.isLastStandingEndRule
        ),
        // И урон крепости на защищенном центральном квартале
        // (checkIfCentralQuarterFortressDamageRule)
        isCentralQuarterFortressDamageRule: Boolean(
          action.payload.isCentralQuarterFortressDamageRule ?? loadedGameState.isCentralQuarterFortressDamageRule
        ),
        // И очки за строения вне городов (checkIfBuildingWinPointsInCitiesRule)
        isBuildingWinPointsInCitiesRule: Boolean(
          action.payload.isBuildingWinPointsInCitiesRule ?? loadedGameState.isBuildingWinPointsInCitiesRule
        ),
      };

      return {
        ...state,
        // Конфигурация сетки из файла поля (радиус/размер гекса) — без нее
        // поля с нестандартным радиусом обрезались бы до радиуса по умолчанию
        gridConfig: loadedState.gridConfig || state.gridConfig,
        hexes: hexes,
        gameState: nextGameState,
      };
    }

    case 'SET_PLAYERS': {
      const players = action.payload.players

      const nextPlayers = createPlayersState(players, getRulesGameType(state.gameState), {
        isPlayerBonusPerCityRule: checkIfPlayerBonusPerCityRule(state.gameState),
      });

      return {
        ...state,
        gameState: {
          ...state.gameState,
          players: nextPlayers
        }
      };
    }

    case 'START_GAME': {
      const {
        timestamp, timerEnabled, fastMode, initiativeShift, isRanked, groundBackground, isParallelSupply,
        isAiMatch, chatEnabled, playerTurnTimeOverrides,
      } = action.payload;

      const hexes = state.hexes;
      const units = state.gameState.units;
      const round = state.gameState.round;
      const players = state.gameState.players;
      const specialBuildings = state.gameState.specialBuildings;
      const buildingsDamage = state.gameState.buildingsDamage;

      // Порядок игроков определяется порядком размещения флагов на поле.
      // Циклический сдвиг (кто получает инициативу первым) считается ВСЕГДА
      // снаружи, до вызова startGame(...) (см. getRandomInitiativeShift) —
      // не здесь: этот action переигрывается при воспроизведении записи
      // матча (REPLAY), и случайность внутри редьюсера при повторном
      // dispatch дала бы другой currentPlayerIndex, чем в исходной партии.
      const turnOrder = players.map(player => player.id);

      // Защита от .slice(undefined): без валидного числа slice(begin)
      // возвращает ПОЛНУЮ копию массива (а не пустую), и итоговый
      // shiftedTurnOrder задваивает каждого игрока — это ломает все,
      // что ключуется по player.id (например, SetupPlayersModal). Такое
      // возможно, только если startGame(...) вызван без getRandomInitiativeShift
      // (пропущенный callsite либо не перезапущенный процесс со старым кодом) —
      // тогда безопасно откатываемся к нулевому сдвигу и громко логируем.
      const isValidShift = Number.isInteger(initiativeShift) && initiativeShift >= 0 && initiativeShift < turnOrder.length;
      if (!isValidShift) {
        console.error('[START_GAME] initiativeShift отсутствует или некорректен, используется 0 (без сдвига). startGame(...) должен вызываться с getRandomInitiativeShift(players.length).', {
          initiativeShift,
          playerCount: turnOrder.length
        });
      }
      const resolvedInitiativeShift = isValidShift ? initiativeShift : 0;

      const shiftedTurnOrder = isEnvConfEnabled(ENV_CONFIG_VARIABLES.REACT_APP_DISABLE_RANDOM_INITIATIVE) ?
        turnOrder : [
          ...turnOrder.slice(resolvedInitiativeShift),
          ...turnOrder.slice(0, resolvedInitiativeShift)
        ];

      // Рассчитываем кэш городов при старте игры
      const citiesCache = calculateCitiesCache(hexes, specialBuildings);

      // Обновляем доходы игроков на основе городов
      const playersWithIncome = updatePlayersIncome(players, citiesCache, units, hexes, specialBuildings, round, buildingsDamage, getRulesGameType(state.gameState), getRuinsIncomeRoundLimit(state.gameState), checkIfCitylessEconomyRule(state.gameState));

      // Определяем индекс первого игрока в порядке ходов (с инициативой)
      const firstPlayerId = shiftedTurnOrder[0];
      const firstPlayerIndex = players.findIndex(p => p.id === firstPlayerId);

      // Create new gameState
      const newGameState = {
        ...state.gameState,
        isStarted: true,
        turnEndTimestamp: timestamp,
        startedAt: timestamp,
        timerEnabled: timerEnabled ?? true,
        fastMode: fastMode ?? false,
        isRanked: Boolean(isRanked),
        // Одновременная фаза снабжения — только если ее включил сервер при
        // старте. Именно Boolean, а не «?? прежнее значение»: флаг просмотренной
        // перед этим записи сетевого матча не должен протечь в локальную игру
        isParallelSupply: Boolean(isParallelSupply),
        isParallelSupplyCommitted: false,
        // Промо-матч против ИИ: признак, модерируемый чат и персональное время
        // хода. Тоже не «?? прежнее значение»: просмотренная перед этим запись
        // ИИ-матча не должна включить чат или чужой таймер в следующей партии
        isAiMatch: Boolean(isAiMatch),
        chatEnabled: Boolean(chatEnabled),
        playerTurnTimeOverrides: sanitizePlayerTurnTimeOverrides(playerTurnTimeOverrides),
        // Записи матчей, сделанные до появления подложки, приходят без нее —
        // тогда оставляем то, что уже лежит в состоянии (сервер дописывает
        // подложку в старые записи при выдаче, см. ensureRecordingGroundBackground)
        groundBackground: groundBackground ?? state.gameState.groundBackground ?? null,
        currentPlayerIndex: firstPlayerIndex,
        players: playersWithIncome,
        turnOrder: shiftedTurnOrder, // Порядок ходов со случайной инициативой
        phase: PHASES.SETUP,
        round: 0,
        phaseCompletedPlayers: [],
        citiesCache: citiesCache,
      };

      return {
        ...state,
        gameState: newGameState,
      };
    }

    case 'SET_HEX_TOKEN': {
      const nextState = internalReducer(state, action);
      const newHexes = nextState.hexes;
      const round = nextState.round;
      const {key, tokenType, price, isCommiting, isFreeAction} = action.payload;

      const gameState = nextState.gameState;
      const units = gameState.units;
      const specialBuildings = gameState.specialBuildings;
      const buildingsDamage = gameState.buildingsDamage;
      const nextInitiativePlayerId = gameState.nextInitiativePlayerId;
      const isInitiativeInterceptedWithIsolationism = gameState.isInitiativeInterceptedWithIsolationism;

      let citiesCache = gameState.citiesCache;
      let players = gameState.players;
      let nextPendingBuildings = [...(gameState.pendingBuildings || [])];

      if (gameState.phase === PHASES.CONSTRUCTION) {
        const currentPlayer = gameState.players[gameState.currentPlayerIndex];

        const hasEnoughCoins = currentPlayer.coins >= price;
        if (!hasEnoughCoins) {
          console.warn(`Player ${currentPlayer.id} does not have enough coins`);
          return state;
        }
        const hasActionsRemaining = checkIfHasRemainingConstructionActions(currentPlayer, nextPendingBuildings, nextInitiativePlayerId, isInitiativeInterceptedWithIsolationism);
        if (!isFreeAction && !hasActionsRemaining) {
          console.warn(`Player ${currentPlayer.id} does not have remaining actions`);
          return state;
        }

        players = players.map(player => {
          return player.id === currentPlayer.id ? {
            ...player,
            coins: player.coins - price,
          } : player;
        });

        // ФАЗА СТРОИТЕЛЬСТВА: добавляем в pendingBuildings, НЕ обновляем citiesCache
        // Сохраняем предыдущий токен (например, дорогу), чтобы можно было восстановить при отмене
        const previousToken = state.hexes[key];

        nextPendingBuildings.push({
          type: CONSTRUCTION_ACTION_TYPES.CONSTRUCTION,
          hexKey: key,
          token: tokenType,
          price,
          previousToken: previousToken || null,
          timestamp: Date.now(),
          isFreeAction,
        });
        // Не обновляем citiesCache и доходы - это произойдет после валидации
      } else {
        // НЕ в фазе строительства - сразу обновляем citiesCache (для setup фазы и т.д.)
        citiesCache = calculateCitiesCache(newHexes, specialBuildings);
        players = updatePlayersIncome(nextState.gameState.players || [], citiesCache, units, newHexes, specialBuildings, round, buildingsDamage, getRulesGameType(state.gameState), getRuinsIncomeRoundLimit(state.gameState), checkIfCitylessEconomyRule(state.gameState));
      }

      if (isCommiting) {
        nextPendingBuildings = nextPendingBuildings.map(building => ({
          ...building,
          isCommited: true
        }))
      }

      return {
        ...nextState,
        gameState: {
          ...nextState.gameState,
          citiesCache: citiesCache,
          players: players,
          pendingBuildings: nextPendingBuildings
        }
      };
    }

    case 'START_NEXT_PHASE': {
      if (isStalePhaseAction(action, state.gameState)) return state;

      const nextState = internalReducer(state, action);

      const phase = state.gameState.phase;

      if (phase === PHASES.SETUP) {
        let nextGameState = nextState.gameState;
        let nextPlayers = nextGameState.players;
        let nextCitiesCache = nextGameState.citiesCache;

        nextPlayers = nextPlayers.map(player => {
          const cities = nextCitiesCache[player.color] || [];

          let citiesWinPoints = 0;
          for (const city of cities) {
            citiesWinPoints += calcCityWinPoints(city);
          }

          // Вычисляем очки, которые добавились благодаря присоединению существующих строений
          // и ключевых точек при размещении стартового квартала.
          const winPointsDiff = citiesWinPoints - cities.length;

          return {
            ...player,
            winPoints: player.winPoints + winPointsDiff
          };
        });

        return {
          ...nextState,
          gameState: {
            ...nextGameState,
            players: nextPlayers,
          }
        }
      }

      return nextState;
    }

    case 'COMPLETE_PHASE': {
      if (isStalePhaseAction(action, state.gameState)) return state;

      // Итог одновременной фазы снабжения уже применен: повторное закрытие до
      // START_NEXT_PHASE ничего не меняет (иначе отряды без снабжения потеряли
      // бы размер дважды)
      const isParallelSupplyClosing = checkIfParallelSupplyPhase(state.gameState);
      if (isParallelSupplyClosing
        && (state.gameState.isParallelSupplyCommitted || !checkIfAllPlayersCompletedPhase(state.gameState))) {
        return state;
      }

      const {timestamp} = action.payload;
      // Сначала вызываем стандартный редьюсер (переход к следующему игроку/фазе)
      const nextState = internalReducer(state, action);

      if (nextState.gameState.phase === PHASES.SETUP) {
        const updatedPlayers = nextState.gameState.players;
        const allPlayersCompleted = nextState.gameState.phaseCompletedPlayers.length === updatedPlayers.length;

        if (allPlayersCompleted || getRulesGameType(nextState.gameState) !== GAME_TYPES.LOCAL) {
          // Когда все игроки завершили фазу SETUP - переносим стартовые кварталы в основное состояние
          const startingQuarters = nextState.gameState.startingQuarters;
          let newHexes = {...state.hexes};

          // Удаляем все флаги с поля
          const filteredHexes = Object.fromEntries(
            Object.entries(newHexes).filter(([_key, value]) => !value.startsWith('flag_'))
          );

          // Добавляем все стартовые центральные кварталы игроков
          Object.entries(startingQuarters).forEach(([playerId, hexKey]) => {
            const player = updatedPlayers.find(p => p.id === playerId);
            if (player) {
              filteredHexes[hexKey] = `central_quarter_${player.color}`;
            }
          });

          const specialBuildings = state.gameState.specialBuildings;
          const buildingsDamage = state.gameState.buildingsDamage;
          const units = nextState.gameState.units;
          const round = nextState.gameState.round;

          // Обновляем кэш городов после размещения всех центральных кварталов
          const citiesCache = calculateCitiesCache(filteredHexes, specialBuildings);

          // Обновляем доходы игроков
          const playersWithIncome = updatePlayersIncome(updatedPlayers, citiesCache, units, filteredHexes, specialBuildings, round, buildingsDamage, getRulesGameType(state.gameState), getRuinsIncomeRoundLimit(state.gameState), checkIfCitylessEconomyRule(state.gameState));

          return {
            ...nextState,
            hexes: filteredHexes,
            gameState: {
              ...nextState.gameState,
              ...(timestamp !== undefined && {turnEndTimestamp: timestamp}),
              citiesCache: citiesCache,
              players: playersWithIncome
            }
          };
        }
      }

      const currentGameState = state.gameState;
      const currentPlayer = currentGameState.players[currentGameState.currentPlayerIndex];
      const round = currentGameState.round;
      const specialBuildings = currentGameState.specialBuildings;

      let nextPlayers = currentGameState.players;
      let nextCitiesCache = currentGameState.citiesCache;
      // Если это фаза строительства и есть pending buildings, обновляем citiesCache
      if (currentGameState.phase === PHASES.CONSTRUCTION) {
        const pendingBuildings = currentGameState.pendingBuildings;

        if (pendingBuildings.length > 0) {
          // Начисляем очки побед за новые рудники и руины
          nextPlayers = nextPlayers.map(player => {
            if (player.id !== currentPlayer.id) {
              return player;
            }

            // Сохраняем старый кэш городов перед обновлением
            const previousCitiesCache = currentGameState.citiesCache;

            // Пересчитываем citiesCache
            const currentHexes = state.hexes;
            nextCitiesCache = calculateCitiesCache(currentHexes, specialBuildings);

            let previousCitiesWinPoints = 0;
            for (const city of (previousCitiesCache[player.color] || [])) {
              previousCitiesWinPoints += calcCityWinPoints(city);
            }

            let nextCitiesWinPoints = 0;
            for (const city of (nextCitiesCache[player.color] || [])) {
              nextCitiesWinPoints += calcCityWinPoints(city);
            }

            // Новые строения, не вошедшие в город игрока (крепость в поле), в
            // очках городов не считаются — не вычитаются и здесь. Старые
            // записи — по-старому (см. checkIfBuildingWinPointsInCitiesRule)
            const countedPendingBuildings = checkIfBuildingWinPointsInCitiesRule(currentGameState)
              ? pendingBuildings.filter(({hexKey}) => checkIfOwner(hexKey, player.color, nextCitiesCache))
              : pendingBuildings;
            const pendingBuildingsWinPoints = calcPendingBuildingsWinPoints(countedPendingBuildings);
            // Вычисляем очки, которые добавились благодаря присоединению существующих строений
            // и ключевых точек (а не благодаря непосредственному строительству в рамках фазы строительства игрока).
            const winPointsDiff = nextCitiesWinPoints
              - pendingBuildingsWinPoints
              - previousCitiesWinPoints;

            return {
              ...player,
              winPoints: player.winPoints + winPointsDiff
            };
          });
        }
      }

      // Применяем штраф за отсутствие снабжения в фазе снабжения.
      // База — отряды ПОСЛЕ внутреннего редьюсера, а не до: при передаче хода в
      // фазе войны он сбрасывает флаги участия в бою и стрельбе
      // (sharedGameStateReducer, COMPLETE_PHASE), и брать здесь состояние «до»
      // значило бы затирать этот сброс. Именно из-за этого отряд, дравшийся
      // ЗАЩИЩАЯСЬ в чужой ход, приходил в свою фазу войны с поднятым флагом и
      // получал право на маневр, ни разу не сразившись в свой ход
      let nextUnits = nextState.gameState.units;
      if (currentGameState.phase === PHASES.SUPPLY) {
        const pendingSupply = currentGameState.pendingSupply;

        // Чьи отряды получают итог: в поочередной фазе — ходящего игрока, при
        // закрытии одновременной — всех игроков разом, по одному и тому же
        // полю (отряды без хозяина среди игроков партии не трогаем)
        const playersById = new Map(currentGameState.players.map((player) => [player.id, player]));
        const getSupplyOwner = (unit) => (isParallelSupplyClosing
          ? playersById.get(unit.ownerId)
          : (unit.ownerId === currentPlayer.id ? currentPlayer : null));

        // Для каждого отряда: если получил снабжение - увеличиваем размер и обрабатываем дополнительное снабжение, если нет - уменьшаем на 2
        nextUnits = nextUnits.map(unit => {
          const owner = getSupplyOwner(unit);
          if (owner) {
            const unitUpdate = calcUnitUpdateBasedOnPendingSupply(unit, owner, pendingSupply);
            const {isSupplyRecruit, ...suppliedUnit} = unit;
            return {
              ...suppliedUnit,
              size: unit.size + unitUpdate.sizeUpdate,
              additionalSupply: unitUpdate.additionalSupply,
            };
          } else {
            return unit;
          }
        });

        nextUnits = nextUnits.filter(u => u.size > 0);
      }

      nextPlayers = updatePlayersIncome(nextPlayers, nextCitiesCache, nextUnits, state.hexes, specialBuildings, round, state.gameState.buildingsDamage, getRulesGameType(state.gameState), getRuinsIncomeRoundLimit(state.gameState), checkIfCitylessEconomyRule(state.gameState));

      // Новые крепости из pendingBuildings добавляем в fortressesUnderConstruction
      const currentFortressesUnderConstruction = currentGameState.fortressesUnderConstruction || [];

      let newFortressHexKeys;
      if (isPlayerPolicy(currentPlayer, POLICIES.FORTIFICATIONISM)) {
        newFortressHexKeys = [];
      } else {
        newFortressHexKeys = currentGameState.phase === PHASES.CONSTRUCTION
          ? currentGameState.pendingBuildings
            .filter(b => b.token === SPECIAL_BUILDING_TOKENS.FORTRESS)
            .map(b => b.hexKey)
          : [];
      }

      // Ход переходит к игроку, который в чужой ход планировал за Элементалей:
      // его вытянутые при планировании жетоны становятся жетонами хода
      const planTokensByPlayerId = nextState.gameState.elementalsPlanTokensByPlayerId || {};
      const nextCurrentPlayer = nextState.gameState.players[nextState.gameState.currentPlayerIndex];
      const nextPlayerPlanTokens = nextState.gameState.phase === PHASES.CONSTRUCTION && nextCurrentPlayer
        && !(nextState.gameState.phaseCompletedPlayers || []).includes(nextCurrentPlayer.id)
        ? planTokensByPlayerId[nextCurrentPlayer.id]
        : undefined;
      const nextPlanTokensByPlayerId = nextPlayerPlanTokens
        ? Object.fromEntries(Object.entries(planTokensByPlayerId).filter(([playerId]) => playerId !== nextCurrentPlayer.id))
        : planTokensByPlayerId;

      return {
        ...nextState,
        gameState: {
          ...nextState.gameState,
          ...(timestamp !== undefined && {turnEndTimestamp: timestamp}),
          citiesCache: nextCitiesCache,
          players: nextPlayers,
          units: nextUnits,
          fortressesUnderConstruction: [...currentFortressesUnderConstruction, ...newFortressHexKeys],
          pendingBuildings: [],
          pendingRecruitmentActions: [],
          pendingMovementActions: [],
          pendingShootingActions: [],
          pendingMeleeDamage: [],
          meleePriorityTargets: {},
          pendingManeuverActions: [],
          pendingSupply: [],
          // FACTIONS
          leprechaunsStealCoinsActions: [],
          elementalsConstructionTokens: nextPlayerPlanTokens || [],
          elementalsPlanTokensByPlayerId: nextPlanTokensByPlayerId,
        }
      };
    }

    case 'UNDO_PENDING_BUILDING': {
      // Восстанавливаем предыдущий токен (например, дорогу) при отмене строительства
      const {hexKey, token, previousToken} = action.payload;

      let nextHexes = state.hexes;
      if (!checkIfSpecialBuilding(token)) {
        nextHexes = {...state.hexes};
        if (previousToken) {
          // Восстанавливаем предыдущий токен (дорогу)
          nextHexes[hexKey] = previousToken;
        } else {
          // Если предыдущего токена нет, удаляем гекс (оставляем пустым)
          delete nextHexes[hexKey];
        }
      }

      const nextState = internalReducer(state, action);

      return {
        ...nextState,
        hexes: nextHexes
      };
    }

    case 'CAPTURE_CITIES': {
      // Экшен без гварда (старая версия) исполняется как раньше — гвард
      // добавлен только для устаревших/повторно доставленных копий (см.
      // captureCities в actions.js), которые иначе пересчитали бы захваты
      // по уже неактуальному раунду/фазе и заново показали бы модалку
      if (isStalePhaseAction(action, state.gameState)) return state;

      // Обновляем citiesCache после захвата городов
      const nextState = internalReducer(state, action);
      const {
        gameState,
        hexes,
      } = nextState;
      const {
        players,
        citiesCache,
        units,
        specialBuildings,
        round,
        buildingsDamage,
      } = gameState;

      const {hexes: nextHexes, capturedCities} = processCityCaptures(citiesCache, hexes, players, units);

      if (capturedCities.length === 0) {
        return nextState;
      } else {
        const nextCitiesCache = calculateCitiesCache(nextHexes, specialBuildings);
        const playersWithUpdatedIncome = updatePlayersIncome(players, nextCitiesCache, units, hexes, specialBuildings, round, buildingsDamage, getRulesGameType(state.gameState), getRuinsIncomeRoundLimit(state.gameState), checkIfCitylessEconomyRule(state.gameState));
        const nextPlayers = updateWinPointsForCitiesCapture(playersWithUpdatedIncome, capturedCities);
        // Окно облета захваченных городов во времени хода (см.
        // getCityCaptureStageExtraMs): от времени захвата, а у старых записей
        // и клиентов без него — от начала текущего этапа
        const capturedAt = action.payload?.timestamp ?? gameState.turnEndTimestamp;

        return {
          ...nextState,
          hexes: nextHexes,
          gameState: {
            ...nextState.gameState,
            citiesCache: nextCitiesCache,
            players: nextPlayers,
            lastCapturedCities: capturedCities,
            // Сводка по игрокам для модалки захвата: города, очки, доход
            lastCityCaptureStats: buildCityCaptureStats(capturedCities, players, nextPlayers),
            cityCaptureTimeWindow: Number.isFinite(capturedAt)
              ? {startsAt: capturedAt, durationMs: getCityCaptureAnimationDurationMs(capturedCities.length)}
              : null,
          }
        };
      }
    }

    case 'SET_CITY_CAPTURE_TIME_WINDOW': {
      return {
        ...state,
        gameState: {
          ...state.gameState,
          cityCaptureTimeWindow: action.payload?.cityCaptureTimeWindow || null,
        }
      };
    }

    case 'SET_COMBAT_RESULTS': {
      return {
        ...state,
        gameState: {
          ...state.gameState,
          combatResults: action.payload.results,
          combatStats: null,
        }
      };
    }

    case 'CLEAR_COMBAT_RESULTS': {
      return {
        ...state,
        gameState: {
          ...state.gameState,
          combatResults: [],
          combatStats: null,
        }
      };
    }

    case 'ADD_PENDING_MOVEMENT_ACTION':
    case 'UNDO_PENDING_MOVEMENT_ACTION':
    case 'ADD_PENDING_RECRUITMENT_ACTION':
    case 'UNDO_PENDING_RECRUITMENT_ACTION':
    case 'APPLY_GOBLINS_TRAP_MOVEMENT': {
      // Обновляем citiesCache после захвата городов. APPLY_GOBLINS_TRAP_MOVEMENT
      // добавлен в эту же группу, т.к. он тоже меняет unit.hexKey (для
      // выжившего) или убирает юнита из units (для погибшего) - ровно то же
      // самое, из-за чего пересчитывается доход после обычного
      // ADD_PENDING_MOVEMENT_ACTION (позиции юнитов блокируют/разблокируют
      // шахты и ключевые точки). Раньше этот кейс падал в default и
      // пропускал пересчёт (найдено внешним ревью ветки) - доход до
      // начисления монет и так пересчитывается заново на COMPLETE_PHASE/
      // CAPTURE_CITIES, поэтому реального рассинхрона не было, но цифра
      // дохода в шапке была неверной до конца хода ходящего игрока
      const nextState = internalReducer(state, action);
      const {
        gameState,
        hexes,
      } = nextState;
      const {
        players,
        citiesCache,
        units,
        round,
        specialBuildings,
        buildingsDamage,
      } = gameState;

      return {
        ...nextState,
        gameState: {
          ...nextState.gameState,
          players: updatePlayersIncome(players, citiesCache, units, hexes, specialBuildings, round, buildingsDamage, getRulesGameType(state.gameState), getRuinsIncomeRoundLimit(state.gameState), checkIfCitylessEconomyRule(state.gameState)),
        }
      };
    }

    case 'APPLY_SHOOTING_ACTIONS': {
      const gameState = state.gameState;
      const currentPlayer = gameState.players[gameState.currentPlayerIndex];
      const pendingShootingActions = gameState.pendingShootingActions
      if (pendingShootingActions.length === 0) {
        return state;
      }
      const hexes = state.hexes;
      const units = gameState.units;
      const specialBuildings = gameState.specialBuildings;

      // Считаем суммарный урон по каждому отряду и определяем кто убил
      const unitDamageMap = {};
      pendingShootingActions.forEach(action => {
        if (!unitDamageMap[action.targetUnitId]) {
          unitDamageMap[action.targetUnitId] = 0;
        }
        unitDamageMap[action.targetUnitId] += action.damage;
      });

      // Применяем урон к отрядам
      const unitsWithAppliedDamage = units.map(unit => {
        const sizeLoss = isUnitFaction(unit, FACTIONS.ORCS) ?
          calculateOrcLoss(unitDamageMap[unit.id]) : unitDamageMap[unit.id];
        if (sizeLoss > 0) {
          const newSize = unit.size - sizeLoss;
          return {
            ...unit,
            size: newSize
          };
        }
        return unit;
      });

      // Находим уничтоженные отряды
      const killedUnits = unitsWithAppliedDamage.filter(unit => unit.size <= 0);
      let nextPlayers = gameState.players;
      const combatResults = [];

      // Начисляем очки победы за уничтоженные отряды
      killedUnits.forEach(unit => {
        const unitConfig = getUnitTypeConfig(unit.type);

        const winPointsForKill = calcWinPointsForKill(currentPlayer, unitConfig, unit.factionId);

        nextPlayers = nextPlayers.map(player => {
          if (player.id === currentPlayer.id) {
            return {
              ...player,
              winPoints: player.winPoints + winPointsForKill,
            };
          }
          return player;
        });

        combatResults.push({
          killedUnit: unit,
          killerPlayerId: currentPlayer.id,
          winPoints: winPointsForKill,
          combatType: COMBAT_TYPE.SHOOTING
        });
      });

      // Оставляем только выжившие отряды
      const nextUnits = unitsWithAppliedDamage.filter(unit => unit.size > 0);

      // --- Обработка урона строениям и их уничтожение ---
      const nextBuildingsDamage = {...gameState.buildingsDamage};
      const nextHexes = {...hexes};
      const nextSpecialBuildings = {...specialBuildings};
      let buildingsDestroyed = false;
      const citiesCache = gameState.citiesCache;

      // Обрабатываем экшены по порядку — важна последовательность,
      // Так как центральные кварталы не получают урон от экшенов,
      // Пока не разрушены полностью базовые кварталы.
      // А также обычные строения получают повреждения только после разрушения специальных строений,
      // расположенных на той же ячейке.
      pendingShootingActions.forEach(action => {
        if (!action.targetHexKey || action.isUnitDamageOnly) return;

        const targetHexKey = action.targetHexKey;
        const hasFortress = nextSpecialBuildings[targetHexKey] === SPECIAL_BUILDING_TOKENS.FORTRESS;

        // Для не-крепостей требуется damage >= 4
        if (!hasFortress && action.damage < D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE) return;

        // Если цель — центральный квартал, проверяем защиту обычными кварталами.
        // Крепость на нем урон принимает и при защите: она — спецстроение, а
        // защита касается самого квартала, по которому бьют уже после
        // разрушения крепости (следующие выстрелы — ее больше нет в
        // nextSpecialBuildings). Старые записи — по-старому
        const isFortressTakingDamage = hasFortress && checkIfCentralQuarterFortressDamageRule(gameState);
        if (!isFortressTakingDamage && checkIfCentralQuarter(nextHexes[targetHexKey])) {
          let cityWithCentralQuarter = null;

          for (const cities of Object.values(citiesCache)) {
            cityWithCentralQuarter = cities.find(city => city.centralQuarter === targetHexKey);
            if (cityWithCentralQuarter) {
              break;
            }
          }

          if (cityWithCentralQuarter.quarter && cityWithCentralQuarter.quarter.length > 0) {
            const hasIntactOrdinaryQuarter = cityWithCentralQuarter.quarter.some(quarterHexKey =>
              !isBuildingDestroyed(quarterHexKey, nextSpecialBuildings, nextBuildingsDamage, [], nextHexes)
            );

            if (hasIntactOrdinaryQuarter) {
              // Игнорируем урон по центральному кварталу,
              // пока не будут разрушены все базовые кварталы
              return;
            }
          }
        }

        // Применяем урон: крепость — 1 к 1,
        // остальные — 1 за выстрел с damage ≥ 4 (проверка выше на порог урона для не крепостей)
        const damageIncrement = hasFortress ? action.damage : 1;
        const existingDamage = nextBuildingsDamage[targetHexKey] || 0;
        const totalDamage = existingDamage + damageIncrement;

        const token = nextHexes[targetHexKey];

        if (token) {
          const specialBuildingType = nextSpecialBuildings[targetHexKey];

          if (!specialBuildingType) {
            // Обычное строение
            if (totalDamage >= 2) {
              // Уничтожить → дорога
              nextHexes[targetHexKey] = TOKENS.ROAD;
              delete nextBuildingsDamage[targetHexKey];
              buildingsDestroyed = true;
            } else if (!UNDESTRUCTIBLE_TOKENS.includes(token)) {
              // Урон = 1
              nextBuildingsDamage[targetHexKey] = 1;
            }
          } else {
            // Специальное строение
            const isFortress = specialBuildingType === SPECIAL_BUILDING_TOKENS.FORTRESS;
            const destructionThreshold = isFortress ? FORTRESS_MAX_DAMAGE : 2;

            if (totalDamage >= destructionThreshold) {
              // Уничтожить спецстроение
              delete nextSpecialBuildings[targetHexKey];
              delete nextBuildingsDamage[targetHexKey];
              buildingsDestroyed = true;
            } else {
              nextBuildingsDamage[targetHexKey] = totalDamage;
            }
          }
        }
      });

      // Пересчитываем citiesCache и win points если были разрушения
      let nextCitiesCache = gameState.citiesCache;
      if (buildingsDestroyed) {
        const previousCitiesCache = gameState.citiesCache;
        nextCitiesCache = calculateCitiesCache(nextHexes, nextSpecialBuildings);

        // Пересчитываем win points для всех игроков, в том числе текущего,
        // так как отряд противника, по которому велась стрельба,
        // мог стоять на ячейке со строением стрелявшего игрока
        nextPlayers = nextPlayers.map(player => {
          const previousCitiesWinPoints = (previousCitiesCache[player.color] || [])
            .reduce((sum, city) => sum + calcCityWinPoints(city), 0);

          const newCitiesWinPoints = (nextCitiesCache[player.color] || [])
            .reduce((sum, city) => sum + calcCityWinPoints(city), 0);

          const winPointsDiff = newCitiesWinPoints - previousCitiesWinPoints;

          return {
            ...player,
            winPoints: Math.max(0, player.winPoints + winPointsDiff)
          };
        });
      }

      // Обновляем доходы
      nextPlayers = updatePlayersIncome(nextPlayers, nextCitiesCache, nextUnits, nextHexes, nextSpecialBuildings, gameState.round, nextBuildingsDamage, getRulesGameType(state.gameState), getRuinsIncomeRoundLimit(state.gameState), checkIfCitylessEconomyRule(state.gameState));

      // Урон стрелявшего — по отрядам: выстрелы по строениям без отряда не в счет
      const shootingDamage = units.reduce((sum, unit) => sum + (unitDamageMap[unit.id] || 0), 0);

      return {
        ...state,
        hexes: nextHexes,
        gameState: {
          ...gameState,
          units: nextUnits,
          players: nextPlayers,
          combatResults,
          combatStats: buildCombatStats({
            results: combatResults,
            players: nextPlayers,
            unitsBefore: units,
            unitsAfter: nextUnits,
            damageDealtByPlayer: {[currentPlayer.id]: shootingDamage},
          }),
          pendingShootingActions: [],
          buildingsDamage: nextBuildingsDamage,
          specialBuildings: nextSpecialBuildings,
          citiesCache: nextCitiesCache,
        }
      };
    }

    case 'FACTION_GOBLINS_DAMAGE_BUILDING': {
      // Кросс-срезовый кейс (как и APPLY_SHOOTING_ACTIONS выше): разрушение
      // строения гоблином меняет hexes (перевод в дорогу) ОДНОВременно с
      // gameState (buildingsDamage/specialBuildings/citiesCache/winPoints) -
      // такое сочетание недоступно per-slice редьюсеру sharedGameStateReducer.js,
      // который отвечает только за срез gameState (см. design.md Decision 17:
      // при первой реализации кейс по ошибке жил там, "hexes" в возвращаемом
      // объекте было мёртвым полем, из-за чего повторное попадание по уже
      // повреждённому строению просто снимало иконку урона, не разрушая его)
      const {unitId, hexKey, roll, success} = action.payload;
      const gameState = state.gameState;
      const hexes = state.hexes;
      const currentPlayer = gameState.players[gameState.currentPlayerIndex];
      // Запоминаем в самой записи действия, была ли целью крепость - строение
      // на этом гексе к моменту чтения истории действий (этап ABILITIES,
      // индикатор "уже применил способность" в HexGridRenderer.renderUnit)
      // может быть уже разрушено, читать specialBuildings[hexKey] постфактум
      // было бы ненадёжно
      const isFortressTarget = gameState.specialBuildings[hexKey] === SPECIAL_BUILDING_TOKENS.FORTRESS;

      const damagingUnit = gameState.units.find(u => u.id === unitId);
      if (!damagingUnit) {
        console.warn('Cannot damage building: unit not found');
        return state;
      }
      if (damagingUnit.hasUsedAbilityThisWarPhase) {
        console.warn('Cannot damage building: unit already used ability this war phase');
        return state;
      }
      if (damagingUnit.ownerId !== currentPlayer.id) {
        console.warn('Cannot damage building: unit does not belong to current player');
        return state;
      }
      if (!isUnitFaction(damagingUnit, FACTIONS.GOBLINS)) {
        console.warn('Cannot damage building: unit is not Goblins');
        return state;
      }
      if (damagingUnit.size < GOBLINS_MIN_UNIT_SIZE_FOR_ABILITY) {
        console.warn('Cannot damage building: unit too small');
        return state;
      }
      if (getHexDistance(damagingUnit.hexKey, hexKey) > GOBLINS_ABILITY_RADIUS) {
        console.warn('Cannot damage building: out of range');
        return state;
      }

      const nextUnits = gameState.units.map(unit =>
        unit.id === unitId ? {...unit, hasUsedAbilityThisWarPhase: true} : unit
      );

      // Отряд, стоящий на гексе строения, НЕ получает урон от этой
      // способности - в отличие от катапульт (см. design.md Decision 7):
      // units меняется только флагом использования способности
      if (!success) {
        return {
          ...state,
          gameState: {
            ...gameState,
            units: nextUnits,
            goblinsAbilityActions: [
              ...gameState.goblinsAbilityActions,
              {type: 'damage_building', unitId, hexKey, roll, success: false, isFortressTarget}
            ],
          }
        };
      }

      // Урон/порог разрушения - единая точка правды с клиентом
      // (GameScene.js предсказывает тот же результат ДО диспатча, чтобы
      // показать верный тост и передать его другим клиентам через payload -
      // см. design.md Decision 17, где расхождение порогов уже ловило баг)
      const {damageIncrement, destroyed: wouldDestroy} = calculateGoblinsBuildingDamageOutcome(
        hexes, gameState.specialBuildings, gameState.buildingsDamage, hexKey, roll
      );

      if (damageIncrement <= 0) {
        // Крепость: бросок 1-2 не наносит урона вовсе
        return {
          ...state,
          gameState: {
            ...gameState,
            units: nextUnits,
            goblinsAbilityActions: [
              ...gameState.goblinsAbilityActions,
              {type: 'damage_building', unitId, hexKey, roll, success: true, damage: 0, isFortressTarget}
            ],
          }
        };
      }

      const nextBuildingsDamage = {...gameState.buildingsDamage};
      const nextHexes = {...hexes};
      const nextSpecialBuildings = {...gameState.specialBuildings};
      const specialBuildingType = nextSpecialBuildings[hexKey];

      let destroyed = false;
      if (wouldDestroy) {
        if (specialBuildingType) {
          delete nextSpecialBuildings[hexKey];
        } else {
          nextHexes[hexKey] = TOKENS.ROAD;
        }
        delete nextBuildingsDamage[hexKey];
        destroyed = true;
      } else {
        nextBuildingsDamage[hexKey] = (gameState.buildingsDamage[hexKey] || 0) + damageIncrement;
      }

      // Пересчитываем citiesCache и win points при разрушении - по тому же
      // принципу, что и после урона катапульт (см. APPLY_SHOOTING_ACTIONS выше)
      let nextCitiesCache = gameState.citiesCache;
      let nextPlayers = gameState.players;
      if (destroyed) {
        const previousCitiesCache = gameState.citiesCache;
        nextCitiesCache = calculateCitiesCache(nextHexes, nextSpecialBuildings);

        nextPlayers = gameState.players.map(player => {
          const previousCitiesWinPoints = (previousCitiesCache[player.color] || [])
            .reduce((sum, city) => sum + calcCityWinPoints(city), 0);
          const newCitiesWinPoints = (nextCitiesCache[player.color] || [])
            .reduce((sum, city) => sum + calcCityWinPoints(city), 0);
          const winPointsDiff = newCitiesWinPoints - previousCitiesWinPoints;
          return {...player, winPoints: Math.max(0, player.winPoints + winPointsDiff)};
        });
      }

      nextPlayers = updatePlayersIncome(
        nextPlayers, nextCitiesCache, nextUnits, nextHexes, nextSpecialBuildings, gameState.round,
        nextBuildingsDamage, getRulesGameType(state.gameState), getRuinsIncomeRoundLimit(state.gameState),
        checkIfCitylessEconomyRule(state.gameState)
      );

      return {
        ...state,
        hexes: nextHexes,
        gameState: {
          ...gameState,
          units: nextUnits,
          players: nextPlayers,
          buildingsDamage: nextBuildingsDamage,
          specialBuildings: nextSpecialBuildings,
          citiesCache: nextCitiesCache,
          goblinsAbilityActions: [
            ...gameState.goblinsAbilityActions,
            {type: 'damage_building', unitId, hexKey, roll, success: true, damage: damageIncrement, destroyed, isFortressTarget}
          ],
        }
      };
    }

    case 'APPLY_MELEE_DAMAGE': {
      // Поздний дубль завершения этапа (клиент и сервер закрыли его почти
      // одновременно, см. applyMeleeDamage): без этой проверки он пересчитал бы
      // итоги по уже пустому урону и затер бы combatResults. Записи старых
      // матчей слепка не несут — для них проверка молчит
      if (isStalePhaseAction(action, state.gameState)) return state;

      const gameState = state.gameState;
      const hexes = state.hexes;
      const pendingMeleeDamage = gameState.pendingMeleeDamage;
      const units = gameState.units;

      const nextBuildingsDamage = {...gameState.buildingsDamage};
      const nextSpecialBuildings = {...gameState.specialBuildings};

      const unitKillerMapping = {};
      const unitsDamageApplied = []

      let buildingsDestroyed = false;
      units.forEach(unit => {
        const {sizeLoss, damageByPlayer, fortressDamage} = calcUnitMeleeDamageWithFortress(
          unit, pendingMeleeDamage, nextSpecialBuildings, nextBuildingsDamage, gameState.fortressesUnderConstruction
        );

        if (fortressDamage > 0) {
          const existingDamage = nextBuildingsDamage[unit.hexKey] || 0;
          const newTotal = existingDamage + fortressDamage;
          if (newTotal >= FORTRESS_MAX_DAMAGE) {
            buildingsDestroyed = true;
            delete nextSpecialBuildings[unit.hexKey];
            delete nextBuildingsDamage[unit.hexKey];
          } else {
            nextBuildingsDamage[unit.hexKey] = newTotal;
          }
        }

        const nextSize = sizeLoss >= unit.size ? 0 : unit.size - sizeLoss;
        unitsDamageApplied.push({
            ...unit,
            size: nextSize,
          }
        );
        if (nextSize === 0) {
          // Определяем нанесшего наибольшее количества урона отряду игрока.
          // Этот игрок считается уничтожившим отряд.
          const damagingPlayerIds = Object.entries(damageByPlayer).sort(
            (a, b) => b[1] - a[1]
          );
          unitKillerMapping[unit.id] = damagingPlayerIds[0][0];
        }
      });

      const killedUnits = [];
      const survivedUnits = [];

      unitsDamageApplied.forEach(unit => {
        if (unit.size === 0) {
          killedUnits.push(unit);
        } else {
          survivedUnits.push(unit);
        }
      });

      // Начисляем win points игрокам, убившим отряды
      let nextPlayers = gameState.players;
      const combatResults = [];

      killedUnits.forEach(killedUnit => {
        const killerPlayerId = unitKillerMapping[killedUnit.id];
        const unitConfig = getUnitTypeConfig(killedUnit.type);
        const winPointsForKill = calcWinPointsForKill(getPlayerById(nextPlayers, killerPlayerId), unitConfig, killedUnit.factionId);

        // Начисляем очки игроку, уничтожившему отряд
        nextPlayers = nextPlayers.map(player => {
          if (player.id === killerPlayerId) {
            return {
              ...player,
              winPoints: player.winPoints + winPointsForKill
            };
          }
          return player;
        });

        combatResults.push({
          killedUnit,
          killerPlayerId: killerPlayerId,
          winPoints: winPointsForKill,
          combatType: COMBAT_TYPE.MELEE,
        })
      });

      const barbarismWinPointsByPlayer = {};
      nextPlayers = nextPlayers.map(player => {
        if (isPlayerPolicy(player, POLICIES.BARBARISM)) {
          const playerUnitsDeliveredDamage = new Set();
          pendingMeleeDamage.forEach((damageAction) => {
            if (damageAction.ownerId === player.id) {
              if (damageAction.damage > 0) {
                playerUnitsDeliveredDamage.add(damageAction.unitId);
              }
            }
          });

          const winPointsForDamage = playerUnitsDeliveredDamage.size * POLICY_BARBARISM_MELEE_DAMAGE_WIN_POINTS_BONUS;
          barbarismWinPointsByPlayer[player.id] = winPointsForDamage;
          return {
            ...player,
            winPoints: player.winPoints + winPointsForDamage,
          };
        }
        return player;
      });

      let nextCitiesCache = gameState.citiesCache;
      if (buildingsDestroyed) {
        const previousCitiesCache = gameState.citiesCache;
        nextCitiesCache = calculateCitiesCache(hexes, nextSpecialBuildings);

        // Пересчитываем win points для всех игроков, в том числе текущего,
        // так как отряд противника, по которому велась стрельба,
        // мог стоять на ячейке со строением стрелявшего игрока
        nextPlayers = nextPlayers.map(player => {
          const previousCitiesWinPoints = (previousCitiesCache[player.color] || [])
            .reduce((sum, city) => sum + calcCityWinPoints(city), 0);

          const newCitiesWinPoints = (nextCitiesCache[player.color] || [])
            .reduce((sum, city) => sum + calcCityWinPoints(city), 0);

          const winPointsDiff = newCitiesWinPoints - previousCitiesWinPoints;

          return {
            ...player,
            winPoints: Math.max(0, player.winPoints + winPointsDiff)
          };
        });
      }

      // Обновляем доходы
      nextPlayers = updatePlayersIncome(nextPlayers, nextCitiesCache, survivedUnits, hexes, nextSpecialBuildings, gameState.round, nextBuildingsDamage, getRulesGameType(state.gameState), getRuinsIncomeRoundLimit(state.gameState), checkIfCitylessEconomyRule(state.gameState));

      return {
        ...state,
        gameState: {
          ...gameState,
          units: survivedUnits,
          players: nextPlayers,
          combatResults,
          combatStats: buildCombatStats({
            results: combatResults,
            players: nextPlayers,
            unitsBefore: units,
            unitsAfter: survivedUnits,
            damageDealtByPlayer: sumMeleeDamageByPlayer(pendingMeleeDamage),
            bonusWinPointsByPlayer: barbarismWinPointsByPlayer,
          }),
          pendingMeleeDamage: [],
          playersWaitedToConfirmCombatStage: [],
          playersConfirmedCombatStage: [],
          meleePriorityTargets: {},
          buildingsDamage: nextBuildingsDamage,
          specialBuildings: nextSpecialBuildings,
        }
      };
    }

    case 'DISTRIBUTE_SUPPLY': {
      const nextState = internalReducer(state, action);

      const {farmHexKey, unitId, unitHexKey, isNotUndoable, isRatmenRecruitment, playerId} = action.payload;
      const gameState = nextState.gameState;
      const hexes = nextState.hexes;
      const units = gameState.units;
      const citiesCache = gameState.citiesCache;
      const specialBuildings = gameState.specialBuildings;
      const buildingsDamage = gameState.buildingsDamage;
      // Снабжение раздает игрок из payload (одновременная фаза), без него — ходящий
      const actorId = resolveSupplyActorId(gameState, action.payload);
      if (checkIfSupplyActorLocked(gameState, actorId)) {
        return state;
      }
      const currentPlayer = gameState.players.find((player) => player.id === actorId);
      if (!currentPlayer) {
        return state;
      }

      // Находим отряд
      const unit = units.find(u => u.id === unitId);
      if (!unit) {
        console.warn('Cannot distribute supply: unit not found');
        return state;
      }

      const pendingSupply = gameState.pendingSupply;
      const unitSupplyCount = pendingSupply.filter(action => action.unitId === unit.id).length;

      // Великаны могут получать базовое снабжение неограниченное количество раз за отряд за ход
      // (каждый раз даёт обычный рост +2/+1). Единственный способ получить для них доп.
      // снабжение (без роста) - отдельное batched-действие DISTRIBUTE_GIANTS_ADDITIONAL_SUPPLY.
      const giveBasicSupply = unitSupplyCount === 0 || isUnitFaction(unit, FACTIONS.GIANTS);
      const giveAdditionalSupply = unitSupplyCount === 1;

      // Определяем размер снабжения, но НЕ применяем его сразу
      // Применение произойдет в при завершении хода игрока.
      const nextPendingSupply = [...gameState.pendingSupply];
      if (giveBasicSupply) {
        const isOnWorkingBarracks = checkIfOnWorkingBarracks(
          unitHexKey, hexes, specialBuildings, currentPlayer, citiesCache, units, buildingsDamage
        );
        const isAdjacentToWorkingMilitaryCamp = checkIfAdjacentToWorkingMilitaryCamp(
          unit.hexKey, currentPlayer, hexes, specialBuildings, citiesCache, units, buildingsDamage
        );
        const isOnEmptyHex = !hexes[unitHexKey];

        nextPendingSupply.push({
          farmHexKey,
          unitId,
          isOnWorkingBarracks,
          isAdjacentToWorkingMilitaryCamp,
          isOnEmptyHex,
          isNotUndoable,
          isRatmenRecruitment,
          ...(playerId && {playerId: actorId}),
        })
      } else if (giveAdditionalSupply) {
        nextPendingSupply.push({
          farmHexKey,
          unitId,
          additionalSupply: giveAdditionalSupply,
          isNotUndoable,
          ...(playerId && {playerId: actorId}),
        })
      }

      return {
        ...nextState,
        gameState: {
          ...gameState,
          pendingSupply: nextPendingSupply,
        }
      };
    }

    case 'DISTRIBUTE_GIANTS_ADDITIONAL_SUPPLY': {
      const nextState = internalReducer(state, action);

      const {farmHexKeys, unitId, playerId} = action.payload;
      const gameState = nextState.gameState;
      const units = gameState.units;

      if (checkIfSupplyActorLocked(gameState, resolveSupplyActorId(gameState, action.payload))) {
        return state;
      }

      const unit = units.find(u => u.id === unitId);
      if (!unit) {
        console.warn('Cannot distribute Giants additional supply: unit not found');
        return state;
      }

      if (!isUnitFaction(unit, FACTIONS.GIANTS)) {
        console.warn('Cannot distribute Giants additional supply: not a Giants unit');
        return state;
      }

      if (!Array.isArray(farmHexKeys) || farmHexKeys.length !== GIANTS_ADDITIONAL_SUPPLY_COST) {
        console.warn('Cannot distribute Giants additional supply: wrong farm count');
        return state;
      }

      if (checkIfUnitHasPendingAdditionalSupply(unitId, gameState.pendingSupply)) {
        console.warn('Cannot distribute Giants additional supply: unit already has additional supply this turn');
        return state;
      }

      if (!checkIfUnitHasPendingBasicSupply(unitId, gameState.pendingSupply)) {
        console.warn('Cannot distribute Giants additional supply: unit has no basic supply this turn');
        return state;
      }

      const nextPendingSupply = [
        ...gameState.pendingSupply,
        {
          farmHexKeys,
          unitId,
          additionalSupply: true,
          isNotUndoable: false,
          ...(playerId && {playerId: resolveSupplyActorId(gameState, action.payload)}),
        }
      ];

      return {
        ...nextState,
        gameState: {
          ...gameState,
          pendingSupply: nextPendingSupply,
        }
      };
    }

    case 'GIANTS_CONVERT_SUPPLY': {
      const nextState = internalReducer(state, action);

      const {unitId, playerId} = action.payload;
      const gameState = nextState.gameState;
      const hexes = nextState.hexes;
      const units = gameState.units;
      const citiesCache = gameState.citiesCache;
      const specialBuildings = gameState.specialBuildings;
      const buildingsDamage = gameState.buildingsDamage;
      const actorId = resolveSupplyActorId(gameState, action.payload);
      if (checkIfSupplyActorLocked(gameState, actorId)) {
        return state;
      }
      const currentPlayer = gameState.players.find((player) => player.id === actorId);
      if (!currentPlayer) {
        return state;
      }
      const actorTag = playerId ? {playerId: actorId} : {};

      const unit = units.find(u => u.id === unitId);
      if (!unit) {
        console.warn('Cannot convert Giants supply: unit not found');
        return state;
      }

      if (!isUnitFaction(unit, FACTIONS.GIANTS)) {
        console.warn('Cannot convert Giants supply: not a Giants unit');
        return state;
      }

      const pendingSupply = gameState.pendingSupply;
      const unitEntries = pendingSupply.filter(s => s.unitId === unitId);
      const additionalEntry = unitEntries.find(s => s.additionalSupply);
      const basicEntries = unitEntries.filter(s => !s.additionalSupply);

      let nextPendingSupply;

      if (additionalEntry) {
        // Дополнительное снабжение -> обратно в 4 базовых (те же фермы,
        // что были потрачены на батч)
        const farmHexKeys = additionalEntry.farmHexKeys || [];
        const isOnWorkingBarracks = checkIfOnWorkingBarracks(
          unit.hexKey, hexes, specialBuildings, currentPlayer, citiesCache, units, buildingsDamage
        );
        const isAdjacentToWorkingMilitaryCamp = checkIfAdjacentToWorkingMilitaryCamp(
          unit.hexKey, currentPlayer, hexes, specialBuildings, citiesCache, units, buildingsDamage
        );
        const isOnEmptyHex = !hexes[unit.hexKey];

        const restoredBasicEntries = farmHexKeys.map(farmHexKey => ({
          farmHexKey,
          unitId,
          isOnWorkingBarracks,
          isAdjacentToWorkingMilitaryCamp,
          isOnEmptyHex,
          isNotUndoable: false,
          ...actorTag,
        }));

        nextPendingSupply = [
          ...pendingSupply.filter(s => s !== additionalEntry),
          ...restoredBasicEntries,
        ];
      } else {
        // 4 базовых -> дополнительное. Минимум 1 базовое должно остаться, поэтому
        // конвертация возможна только от 5 базовых снабжений и выше
        if (basicEntries.length < GIANTS_ADDITIONAL_SUPPLY_COST + 1) {
          console.warn('Cannot convert Giants supply: not enough basic supply');
          return state;
        }

        const entriesToConvert = basicEntries.slice(0, GIANTS_ADDITIONAL_SUPPLY_COST);
        const farmHexKeys = entriesToConvert.map(entry => entry.farmHexKey);

        nextPendingSupply = [
          ...pendingSupply.filter(s => !entriesToConvert.includes(s)),
          {
            farmHexKeys,
            unitId,
            additionalSupply: true,
            isNotUndoable: false,
            ...actorTag,
          }
        ];
      }

      return {
        ...nextState,
        gameState: {
          ...gameState,
          pendingSupply: nextPendingSupply,
        }
      };
    }

    case 'CALCULATE_MELEE_COMBAT_DAMAGE': {
      const {combatUnits, unitToEnemiesMapping} = action.payload.meleeCombatInput;
      const rerollUnitId = action.payload.rerollUnitId;
      const priorityTargetForUnit = action.payload.priorityTargetForUnit;

      const gameState = state.gameState;
      const hexes = state.hexes;
      const players = gameState.players;
      const units = gameState.units;
      const currentPlayer = gameState.players[gameState.currentPlayerIndex];
      const turnOrder = gameState.turnOrder;
      const citiesCache = gameState.citiesCache;
      const specialBuildings = gameState.specialBuildings;
      const buildingsDamage = gameState.buildingsDamage;
      let nextMeleePriorityTargets = gameState.meleePriorityTargets;
      const prevPendingMeleeDamage = gameState.pendingMeleeDamage;

      if (priorityTargetForUnit) {
        nextMeleePriorityTargets = {
          ...nextMeleePriorityTargets,
          ...priorityTargetForUnit,
        }
      }

      // Для каждого отряда ближнего боя создаём маркер урона
      const pendingMeleeDamage = [];

      const playersWaitedToConfirmCombatStageSet = new Set();

      const remainingUnitsDamage = {};
      combatUnits.forEach(unit => {
        if (unit.ownerId !== NEUTRAL_PLAYER_ID) {
          playersWaitedToConfirmCombatStageSet.add(unit.ownerId);
        }

        const specialBuilding = specialBuildings[unit.hexKey];
        const isFortressUnderConstruction = gameState.fortressesUnderConstruction?.includes(unit.hexKey);
        let damageForElimination = unit.size;
        if (specialBuilding === SPECIAL_BUILDING_TOKENS.FORTRESS && !isFortressUnderConstruction) {
          const remainingFortressDurability = FORTRESS_MAX_DAMAGE - (buildingsDamage[unit.hexKey] || 0)
          if (isUnitFaction(unit, FACTIONS.ORCS)) {
            remainingUnitsDamage[unit.id] = calculateOrcMeleeDamageForElimination(unit.size, remainingFortressDurability);
          } else {
            remainingUnitsDamage[unit.id] = damageForElimination > remainingFortressDurability ?
              remainingFortressDurability * FORTRESS_MELEE_DAMAGE_RATION + (damageForElimination - remainingFortressDurability)
              : damageForElimination * FORTRESS_MELEE_DAMAGE_RATION;
          }
        } else {
          if (isUnitFaction(unit, FACTIONS.ORCS)) {
            remainingUnitsDamage[unit.id] = calculateOrcMeleeDamageForElimination(unit.size, 0);
          } else {
            remainingUnitsDamage[unit.id] = damageForElimination;
          }
        }
      });

      // Сортируем отряды в порядке возрастания, чтобы понизить вероятность того,
      // что некоторая доля урона отрядов будет заблокирована автораспределением урона.
      combatUnits.sort((a, b) => a.size - b.size);

      // Сортируем отряды в порядке очередности хода игроков от текущего игрока.
      // Чтобы автораспределение урона отдавало приоритет текущему игроку и следующим по порядку от него.
      const turnOrderFromCurrentPlayer = getTurnOrderFromPlayer(currentPlayer.id, turnOrder);
      const playersOrderMapping = {
        // У нейтрального игрока последний приоритет урона
        [NEUTRAL_PLAYER_ID]: 9999
      };
      turnOrderFromCurrentPlayer.forEach((playerId, index) => {
        playersOrderMapping[playerId] = index;
      });
      combatUnits.sort((a, b) => {
        const aTurnOrder = playersOrderMapping[a.ownerId]
        const bTurnOrder = playersOrderMapping[b.ownerId]

        return aTurnOrder - bTurnOrder;
      });

      // Сортируем отряды в порядке количества смежных им противников.
      // Чтобы отряды только с одним смежным отрядом противника обязательно наносили весь возможный урон.
      combatUnits.sort((a, b) => {
        return unitToEnemiesMapping[a.id].length - unitToEnemiesMapping[b.id].length;
      })

      combatUnits.forEach(unit => {
        const {diceRoll1, diceRoll2, diceRoll3, diceRoll4} = unit;
        // У селестиалов 4 кости — в урон идут две лучшие (getMeleeCountedDiceRolls)
        const [countedDiceRoll1, countedDiceRoll2] = getMeleeCountedDiceRolls(unit);

        const owner = getPlayerById(players, unit.ownerId);
        const meleeContext = {
          owner, citiesCache, specialBuildings, buildingsDamage, hexes, units, players
        };
        const {damage, bonus} = calcMeleeDamage(unit, countedDiceRoll1, countedDiceRoll2, meleeContext);

        const priorityEnemyUnitId = nextMeleePriorityTargets[unit.id];

        const neighborEnemies = unitToEnemiesMapping[unit.id].sort((a, b) => {
          if (priorityEnemyUnitId) {
            if (a.id === priorityEnemyUnitId) {
              return -1;
            } else if (b.id === priorityEnemyUnitId) {
              return 1;
            }
          }

          return a.size - b.size;
        });

        const damageDistribution = {};
        let remainingDamage = damage;

        neighborEnemies.forEach(enemy => {
          const remainingDamageForUnit = remainingUnitsDamage[enemy.id];
          const damageToEnemy = remainingDamage > remainingDamageForUnit ? remainingDamageForUnit : remainingDamage;
          remainingDamage -= damageToEnemy;
          remainingUnitsDamage[enemy.id] -= damageToEnemy;
          damageDistribution[enemy.id] = damageToEnemy;
        })

        let hasRerolled;
        if (rerollUnitId === unit.id) {
          hasRerolled = true;
        } else {
          const prevAction = prevPendingMeleeDamage.find(prevAction => {
            return prevAction.unitId === unit.id;
          });
          if (prevAction && prevAction.hasRerolled) {
            hasRerolled = true;
          }
        }

        pendingMeleeDamage.push({
          unitId: unit.id,
          ownerId: unit.ownerId,
          hasRerolled,
          bonus,
          diceRoll1,
          diceRoll2,
          ...(diceRoll3 !== undefined && diceRoll4 !== undefined ? {diceRoll3, diceRoll4} : {}),
          damage,
          damageDistribution,
        });
      });

      // Отмечаем все отряды, участвующие в ближнем бою
      const combatUnitIds = new Set(combatUnits.map(unit => unit.id));

      // Сбрасываем дополнительное снабжение отряда,
      // если это перерасчет урона на основании переброса отряда
      let nextUnits = units.map(unit => {
        const updates = {};

        if (combatUnitIds.has(unit.id)) {
          updates.hasFoughtThisWarPhase = true;
        }

        if (rerollUnitId && unit.id === rerollUnitId) {
          updates.additionalSupply = false;
        }

        if (Object.keys(updates).length === 0) {
          return unit;
        }

        return {...unit, ...updates};
      });

      return {
        ...state,
        gameState: {
          ...gameState,
          units: nextUnits,
          pendingMeleeDamage,
          meleePriorityTargets: nextMeleePriorityTargets,
          playersWaitedToConfirmCombatStage: [...playersWaitedToConfirmCombatStageSet],
        }
      };
    }

    default:
      if (SUPPLY_SELECTION_ACTION_TYPES.includes(action.type)) {
        return internalReducer(state, withResolvedSupplyActor(action, state.gameState));
      }
      return internalReducer(state, action);
  }
};

export default sharedStateReducer;